import { Accordion, Divider, Flex } from "@chakra-ui/react";
import AccordianItemWrapper from "./AccordianItemWrapper";
import WrapUnwrapAccordian from "./WrapUnwrapAccordian";
import BridgeAccordian from "./BridgeAccordian";
import useBridgeWrapConfig from "@hooks/useBridgeWrapConfig";
import useSwapTokensBalances from "@hooks/useSwapTokensBalances";
import { formatUnits } from "viem";
import { formatNumber } from "utils";
import { ALL_STEPS, BW_VARIANT, TChainSelection } from "./types";
import { toFixedWithoutRounding } from "@constants/common";
import useTokenBalanceForChain from "@hooks/useTokenBalanceForChain";
import { updateUnwrapTxnState } from "../../store/slices/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

interface BridgeJourneyStepsProps {
  sourceChain: TChainSelection;
  destinationChain: TChainSelection;
}

const BridgeJourneySteps = ({
  sourceChain,
  destinationChain,
}: BridgeJourneyStepsProps) => {
  const BRIDGE_WRAP_DATA = useBridgeWrapConfig(BW_VARIANT.RSETH);

  const { wrsETHBalance, rsETHBalance } = useSwapTokensBalances(
    sourceChain.id!
  );

  const dispatch = useDispatch();

  const txnSuccess = useSelector(
    (state: any) => state?.global?.unwrapTxnState?.success
  );

  useEffect(() => {
    if (txnSuccess) {
      dispatch(
        updateUnwrapTxnState({
          success: false,
        })
      );
    }
  }, [sourceChain, destinationChain]);

  // Destructure balances conditionally
  const { xTokenTokenBalance } = useTokenBalanceForChain(sourceChain.id);
  const hasWrsETH =
    Number(toFixedWithoutRounding(formatUnits(wrsETHBalance, 18), 4)) > 0;

  const sourceBalance =
    sourceChain.id === 1 || sourceChain.id === 42141
      ? Number(
          formatNumber(
            Number(xTokenTokenBalance),
            false,
            Number(xTokenTokenBalance) < 0.0001 ? 6 : 4,
            true
          )
        )
      : Number(formatUnits(rsETHBalance, 18));

  const assetSelected = hasWrsETH
    ? BRIDGE_WRAP_DATA.assets[1]
    : BRIDGE_WRAP_DATA.assets[0];

  const generateSteps = () => {
    const STEPS: string[] = [];

    if (
      (sourceChain.id === 1 && destinationChain.id === 42161) ||
      (sourceChain.id === 42161 && destinationChain.id === 1)
    ) {
      STEPS.push(ALL_STEPS.BRIDGE);
    } else if (sourceChain.id === destinationChain.id) {
      STEPS.push(ALL_STEPS.WRAP);
    } else if (
      (sourceChain.id === 1 || sourceChain.id === 42161) &&
      destinationChain.id !== 1 &&
      destinationChain.id !== 42161
    ) {
      STEPS.push(ALL_STEPS.BRIDGE);
      STEPS.push(ALL_STEPS.WRAP);
    } else if (
      sourceChain.id !== 1 &&
      sourceChain.id !== 42161 &&
      (destinationChain.id === 1 || destinationChain.id === 42161)
    ) {
      if (hasWrsETH || txnSuccess) STEPS.push(ALL_STEPS.UNWRAP);
      STEPS.push(ALL_STEPS.BRIDGE);
    } else if (
      sourceChain.id !== 1 &&
      sourceChain.id !== 42161 &&
      destinationChain.id !== 1 &&
      destinationChain.id !== 42161
    ) {
      if (hasWrsETH || txnSuccess) STEPS.push(ALL_STEPS.UNWRAP);
      STEPS.push(ALL_STEPS.BRIDGE);
      STEPS.push(ALL_STEPS.WRAP);
    } else {
      STEPS.push(ALL_STEPS.BRIDGE);
    }

    return STEPS;
  };

  const renderStep = (step: string, idx: number) => {
    switch (step) {
      case ALL_STEPS.BRIDGE:
        return (
          <AccordianItemWrapper
            prefixLabel={`Step ${idx}:`}
            label={`Bridge your tokens`}
            element={
              <BridgeAccordian
                fromChain={sourceChain}
                toChain={destinationChain}
                fromSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                toSymbol={BRIDGE_WRAP_DATA.assets[0].id}
                tokenAmount={
                  sourceBalance
                    ? +toFixedWithoutRounding(sourceBalance.toString(), 4)
                    : 0
                }
              />
            }
          />
        );
      case ALL_STEPS.WRAP:
        return (
          <AccordianItemWrapper
            prefixLabel={`Step ${idx}:`}
            label={`Wrap your tokens`}
            element={
              <WrapUnwrapAccordian
                type="wrap"
                sourceChain={sourceChain}
                destinationChain={destinationChain}
              />
            }
          />
        );
      case ALL_STEPS.UNWRAP:
        return (
          <AccordianItemWrapper
            prefixLabel={`Step ${idx}:`}
            label={`Unwrap your tokens`}
            element={
              <WrapUnwrapAccordian
                type="unwrap"
                sourceChain={sourceChain}
                destinationChain={destinationChain}
              />
            }
          />
        );
      default:
        return null;
    }
  };

  if (
    (sourceChain.id === 1 && destinationChain.id === 1) ||
    (sourceChain.id === 42161 && destinationChain.id === 42161)
  ) {
    return null;
  }

  return (
    <Flex direction="column" gap="10px" width={"100%"}>
      {/* {hasWrsETH && renderMessage()} */}
      {assetSelected?.id && (
        <Accordion
          w="100%"
          allowMultiple
          defaultIndex={[0, 1, 2]}
          display="flex"
          flexDirection="column"
        >
          {generateSteps()?.map((step, index) => {
            return (
              <>
                {index > 0 && <Divider my={2} bg={"#B3CECE"} height="1px" />}
                {renderStep(step, index + 1)}
              </>
            );
          })}
        </Accordion>
      )}

      {generateSteps().length > 0 && (
        <Divider my={2} bg={"#B3CECE"} height="1px" />
      )}
    </Flex>
  );
};

export default BridgeJourneySteps;
